import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react'
import { pick as pickText, t as translate, type Key, type Lang } from '@/core/i18n'
import { loadSettings, saveSettings, type Settings } from '@/core/settings'

export interface SettingsValue {
  settings: Settings
  update: (change: Partial<Settings>) => void
  /** Text der Oberfläche in der gewählten Sprache. */
  t: (key: Key, vars?: Record<string, string | number>) => string
  /** Zweisprachiger Datensatz in der gewählten Sprache. */
  pick: (text: { de: string; en: string }) => string
  lang: Lang
}

const SettingsContext = createContext<SettingsValue | undefined>(undefined)

export function SettingsProvider({ children }: { children: ReactNode }) {
  const [settings, setSettings] = useState<Settings>(() => loadSettings())

  const update = useCallback((change: Partial<Settings>) => {
    setSettings((old) => {
      const next = { ...old, ...change }
      saveSettings(next)
      return next
    })
  }, [])

  const value = useMemo<SettingsValue>(
    () => ({
      settings,
      update,
      lang: settings.lang,
      t: (key, vars) => translate(settings.lang, key, vars),
      pick: (text) => pickText(text, settings.lang),
    }),
    [settings, update],
  )

  return <SettingsContext.Provider value={value}>{children}</SettingsContext.Provider>
}

export function useSettings(): SettingsValue {
  const value = useContext(SettingsContext)
  if (!value) throw new Error('useSettings ohne SettingsProvider')
  return value
}
